import { useState, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';

const Avatar = ({
    src,
    firstName,
    lastName,
    variant = 'circle',
    size = 40,
    style = {},
    ...props
}) => {
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
        setImageError(false);
    }, [src]);

    const initials = useMemo(() => {
        const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
        const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
        return `${firstInitial}${lastInitial}`;
    }, [firstName, lastName]);

    const showImage = src && !imageError;

    return (
        <AvatarContainer
            $variant={variant}
            $size={size}
            $isText={!showImage}
            style={style}
            {...props}
            title={`${firstName} ${lastName}`}
        >
            {showImage ? (
                <StyledImage
                    src={src}
                    alt={`${firstName} ${lastName}`}
                    onError={() => setImageError(true)}
                />
            ) : (
                <InitialsText style={{fontSize: `${size * .50}px`}}>{initials}</InitialsText>
            )}
        </AvatarContainer>
    );
};

Avatar.propTypes = {
    src: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(['circle', 'square']),
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default Avatar;

const AvatarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  width: ${props => typeof props.$size === 'number' ? `${props.$size}px` : props.$size};
  height: ${props => typeof props.$size === 'number' ? `${props.$size}px` : props.$size};

  ${props => props.$variant === 'circle' ? css`
    border-radius: 50%;
  ` : css`
    border-radius: 12px;
  `}

  ${props => props.$isText && css`
    background-color: ${props.theme.colors.primary?.light || '#dbeafe'};
    color: ${props.theme.colors.primary?.main || '#2563eb'};
    border: 1px solid #e2e8f0;
    font-weight: 600;
  `}
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const InitialsText = styled.span`
  font-size: inherit;
  line-height: 1;
  user-select: none;
  color: white;
`;
